/**
 * Funnel Helpers - Utility functions for funnel builder
 */

// Funnel stages
export const FUNNEL_STAGES = {
    TOFU: { label: 'Awareness', color: 'var(--color-info)' },
    MOFU: { label: 'Consideration', color: 'var(--color-warning)' },
    BOFU: { label: 'Conversion', color: 'var(--color-success)' }
};

// Objective to funnel stage mapping
export const OBJECTIVE_TO_STAGE = {
    AWARENESS: 'TOFU',
    REACH: 'TOFU',
    BRAND_AWARENESS: 'TOFU',
    TRAFFIC: 'TOFU',
    ENGAGEMENT: 'MOFU',
    VIDEO_VIEWS: 'MOFU',
    LEAD_GENERATION: 'MOFU',
    LEADS: 'MOFU',
    MESSAGES: 'MOFU',
    CONVERSIONS: 'BOFU',
    CATALOG_SALES: 'BOFU',
    STORE_VISITS: 'BOFU',
    SALES: 'BOFU'
};

/**
 * Calculate total daily budget from campaign nodes
 */
export const calculateTotalBudget = (nodes) => {
    return nodes
        .filter(n => n.type === 'campaign')
        .reduce((sum, n) => {
            const budget = n.data.budget;
            if (!budget) return sum;

            // Convert lifetime budget to daily equivalent (rough estimate)
            if (budget.type === 'lifetime' && n.data.schedule?.start && n.data.schedule?.end) {
                const start = new Date(n.data.schedule.start);
                const end = new Date(n.data.schedule.end);
                const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) || 1;
                return sum + (budget.amount / days);
            }

            return sum + (budget.amount || 0);
        }, 0);
};

/**
 * Calculate budget by funnel stage
 */
export const calculateBudgetByStage = (nodes) => {
    const budgetByStage = { TOFU: 0, MOFU: 0, BOFU: 0 };

    nodes
        .filter(n => n.type === 'campaign')
        .forEach(n => {
            const stage = n.data.funnelStage || 'TOFU';
            const amount = n.data.budget?.amount || 0;
            budgetByStage[stage] = (budgetByStage[stage] || 0) + amount;
        });

    return budgetByStage;
};

/**
 * Calculate budget by platform
 */
export const calculateBudgetByPlatform = (nodes) => {
    const budgetByPlatform = {};

    nodes
        .filter(n => n.type === 'campaign')
        .forEach(n => {
            const platform = n.data.platform || 'meta';
            const amount = n.data.budget?.amount || 0;
            budgetByPlatform[platform] = (budgetByPlatform[platform] || 0) + amount;
        });

    return budgetByPlatform;
};

/**
 * Validate funnel structure
 */
export const validateFunnelStructure = (nodes, edges) => {
    const errors = [];
    const warnings = [];

    // Check for orphan nodes (no connections)
    const connectedNodes = new Set();
    edges.forEach(e => {
        connectedNodes.add(e.source);
        connectedNodes.add(e.target);
    });

    const orphanNodes = nodes.filter(n =>
        n.type !== 'campaign' && !connectedNodes.has(n.id)
    );

    if (orphanNodes.length > 0) {
        warnings.push(`${orphanNodes.length} node(s) are not connected to any campaign`);
    }

    // Check for cycles (would cause infinite loops)
    const hasCycle = detectCycle(nodes, edges);
    if (hasCycle) {
        errors.push('Funnel structure contains circular references');
    }

    // Check hierarchy integrity
    edges.forEach(edge => {
        const source = nodes.find(n => n.id === edge.source);
        const target = nodes.find(n => n.id === edge.target);

        if (!source || !target) return;

        // Valid hierarchies: campaign -> adset, adset -> ad, campaign -> campaign (retargeting)
        const validConnections = {
            campaign: ['adset', 'campaign'],
            adset: ['ad', 'abtest'],
            abtest: ['ad']
        };

        const allowed = validConnections[source.type] || [];
        if (!allowed.includes(target.type)) {
            warnings.push(
                `Invalid connection: ${source.type} cannot connect to ${target.type}`
            );
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};

/**
 * Detect cycles in the graph using DFS
 */
const detectCycle = (nodes, edges) => {
    const adjacencyList = {};
    nodes.forEach(n => {
        adjacencyList[n.id] = [];
    });
    edges.forEach(e => {
        if (adjacencyList[e.source]) {
            adjacencyList[e.source].push(e.target);
        }
    });

    const visited = new Set();
    const recursionStack = new Set();

    const dfs = (nodeId) => {
        visited.add(nodeId);
        recursionStack.add(nodeId);

        for (const neighbor of adjacencyList[nodeId] || []) {
            if (!visited.has(neighbor)) {
                if (dfs(neighbor)) return true;
            } else if (recursionStack.has(neighbor)) {
                return true;
            }
        }

        recursionStack.delete(nodeId);
        return false;
    };

    for (const node of nodes) {
        if (!visited.has(node.id)) {
            if (dfs(node.id)) return true;
        }
    }

    return false;
};

/**
 * Group nodes by funnel stage
 */
export const groupNodesByStage = (nodes) => {
    const grouped = { TOFU: [], MOFU: [], BOFU: [] };

    nodes.forEach(node => {
        const stage = node.data.funnelStage || 'TOFU';
        if (grouped[stage]) {
            grouped[stage].push(node);
        }
    });

    return grouped;
};

/**
 * Generate unique node ID
 */
export const generateNodeId = (type) => {
    return `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get suggested funnel stage based on objective
 */
export const getSuggestedStage = (objective) => {
    return OBJECTIVE_TO_STAGE[objective] || 'TOFU';
};

/**
 * Calculate estimated reach based on targeting
 */
export const estimateReach = (targeting) => {
    // Base reach (placeholder - would need actual API integration)
    let baseReach = 10000000;

    // Adjust for age range
    if (targeting.ageMin && targeting.ageMax) {
        const ageRange = targeting.ageMax - targeting.ageMin;
        baseReach *= (ageRange / 47); // 18-65 is full range
    }

    // Adjust for gender
    if (targeting.genders?.length === 1) {
        baseReach *= 0.5;
    }

    // Adjust for interests
    if (targeting.interests?.length > 0) {
        baseReach *= Math.max(0.1, 1 - (targeting.interests.length * 0.05));
    }

    // Adjust for locations
    if (targeting.locations?.length > 0) {
        // Assume each location is roughly 5% of total
        baseReach *= Math.min(1, targeting.locations.length * 0.05);
    }

    return Math.round(baseReach);
};

/**
 * Format budget for display
 */
export const formatBudget = (amount, type = 'daily') => {
    if (!amount) return '$0';
    const formatted = amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });
    return `${formatted}/${type === 'daily' ? 'day' : 'total'}`;
};

/**
 * Get funnel statistics
 */
export const getFunnelStats = (nodes, edges) => {
    const campaigns = nodes.filter(n => n.type === 'campaign');
    const adSets = nodes.filter(n => n.type === 'adset');
    const ads = nodes.filter(n => n.type === 'ad');
    const abTests = nodes.filter(n => n.type === 'abtest');

    const budgetByStage = calculateBudgetByStage(nodes);
    const totalBudget = calculateTotalBudget(nodes);

    return {
        totalNodes: nodes.length,
        campaigns: campaigns.length,
        adSets: adSets.length,
        ads: ads.length,
        abTests: abTests.length,
        connections: edges.length,
        totalBudget,
        budgetByStage,
        stages: {
            TOFU: nodes.filter(n => n.data.funnelStage === 'TOFU').length,
            MOFU: nodes.filter(n => n.data.funnelStage === 'MOFU').length,
            BOFO: nodes.filter(n => n.data.funnelStage === 'BOFU').length
        }
    };
};

/**
 * Create retargeting edge between campaigns
 */
export const createRetargetingEdge = (sourceCampaignId, targetCampaignId) => {
    return {
        id: `retarget_${sourceCampaignId}_${targetCampaignId}`,
        source: sourceCampaignId,
        target: targetCampaignId,
        sourceHandle: 'retarget-out',
        targetHandle: 'retarget-in',
        type: 'smart',
        animated: true,
        style: { strokeDasharray: '5 5', stroke: 'var(--color-warning)' }
    };
};

/**
 * Auto-layout nodes using type-based hierarchy levels
 *
 * Layout Strategy:
 * 1. Position campaigns first, sorted by funnel stage (TOFU left → BOFU right)
 * 2. Bridge nodes (RetargetingPool, Segment) are positioned based on their connections:
 *    - If fed by Campaign A and outputs to Campaign B: positioned BETWEEN them
 *    - If fed by Campaign B and outputs within Campaign B: positioned to the RIGHT
 * 3. AdSets are positioned centered under their inputs (campaign + any bridge nodes)
 * 4. Ads/ABTests are positioned under their parent AdSet
 */
export const autoLayoutNodes = (nodes, edges) => {
    if (!nodes.length) return [];

    // Node dimensions
    const nodeDimensions = {
        campaign: { width: 300, height: 200 },
        adset: { width: 260, height: 180 },
        ad: { width: 220, height: 300 },
        abtest: { width: 320, height: 240 },
        segment: { width: 260, height: 200 },
        retargetingPool: { width: 240, height: 180 }
    };

    // Fixed vertical levels based on node type
    const typeToLevel = {
        campaign: 0,
        retargetingPool: 1,
        segment: 2,
        adset: 3,
        abtest: 4,
        ad: 5
    };

    const horizontalGap = 60;
    const verticalGap = 60;
    const campaignGap = 400; // Gap between campaign columns
    const startX = 100;
    const startY = 80;

    // Calculate Y position for each level
    const levelHeights = [
        nodeDimensions.campaign.height,
        nodeDimensions.retargetingPool.height,
        nodeDimensions.segment.height,
        nodeDimensions.adset.height,
        nodeDimensions.abtest.height,
        nodeDimensions.ad.height
    ];

    const levelY = {};
    let cumulativeY = startY;
    for (let i = 0; i <= 5; i++) {
        levelY[i] = cumulativeY;
        cumulativeY += levelHeights[i] + verticalGap;
    }

    const bridgeNodeTypes = new Set(['retargetingPool', 'segment']);
    const nodeIds = new Set(nodes.map(n => n.id));
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // Build edge maps
    const edgesBySource = {};
    const edgesByTarget = {};
    edges.forEach(edge => {
        if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) return;
        if (!edgesBySource[edge.source]) edgesBySource[edge.source] = [];
        if (!edgesByTarget[edge.target]) edgesByTarget[edge.target] = [];
        edgesBySource[edge.source].push(edge.target);
        edgesByTarget[edge.target].push(edge.source);
    });

    // Helper: find campaign that feeds a node (trace back through edges)
    const findSourceCampaign = (nodeId, visited = new Set()) => {
        if (visited.has(nodeId)) return null;
        visited.add(nodeId);

        const node = nodeMap.get(nodeId);
        if (!node) return null;
        if (node.type === 'campaign') return nodeId;

        const sources = edgesByTarget[nodeId] || [];
        for (const sourceId of sources) {
            const result = findSourceCampaign(sourceId, visited);
            if (result) return result;
        }
        return null;
    };

    // Helper: find campaign that a node outputs to (trace forward, skip bridge nodes)
    const findTargetCampaign = (nodeId, visited = new Set()) => {
        if (visited.has(nodeId)) return null;
        visited.add(nodeId);

        const targets = edgesBySource[nodeId] || [];
        for (const targetId of targets) {
            const targetNode = nodeMap.get(targetId);
            if (!targetNode) continue;

            if (targetNode.type === 'campaign') return targetId;

            if (targetNode.type === 'adset') {
                // Find the campaign that owns this adset
                const adsetSources = edgesByTarget[targetId] || [];
                for (const adsetSource of adsetSources) {
                    const sourceNode = nodeMap.get(adsetSource);
                    if (sourceNode?.type === 'campaign') return adsetSource;
                }
            }

            // Continue tracing through bridge nodes
            if (bridgeNodeTypes.has(targetNode.type)) {
                const result = findTargetCampaign(targetId, visited);
                if (result) return result;
            }
        }
        return null;
    };

    // Step 1: Position campaigns by funnel stage
    const stageOrder = { 'TOFU': 0, 'MOFU': 1, 'BOFU': 2 };
    const campaigns = nodes.filter(n => n.type === 'campaign');
    campaigns.sort((a, b) => {
        const aStage = a.data?.funnelStage || 'TOFU';
        const bStage = b.data?.funnelStage || 'TOFU';
        const stageCompare = (stageOrder[aStage] ?? 0) - (stageOrder[bStage] ?? 0);
        if (stageCompare !== 0) return stageCompare;
        return (a.data?.name || '').localeCompare(b.data?.name || '');
    });

    const positionedNodes = {};
    const campaignPositions = {}; // campaignId -> { x, centerX }

    // Position campaigns with consistent spacing
    let campaignX = startX;
    campaigns.forEach(campaign => {
        const width = nodeDimensions.campaign.width;
        positionedNodes[campaign.id] = {
            ...campaign,
            position: { x: campaignX, y: levelY[0] }
        };
        campaignPositions[campaign.id] = {
            x: campaignX,
            centerX: campaignX + width / 2
        };
        campaignX += campaignGap;
    });

    // Step 2: Analyze and position bridge nodes
    const bridgeNodes = nodes.filter(n => bridgeNodeTypes.has(n.type));

    bridgeNodes.forEach(bridgeNode => {
        const sourceCampaign = findSourceCampaign(bridgeNode.id);
        const targetCampaign = findTargetCampaign(bridgeNode.id);

        const level = typeToLevel[bridgeNode.type];
        const width = nodeDimensions[bridgeNode.type]?.width || 200;
        let x;

        if (sourceCampaign && targetCampaign && sourceCampaign !== targetCampaign) {
            // Bridge between two different campaigns - position BETWEEN them
            const sourcePos = campaignPositions[sourceCampaign];
            const targetPos = campaignPositions[targetCampaign];

            if (sourcePos && targetPos) {
                // Position between the two campaigns, closer to target
                x = sourcePos.centerX + (targetPos.centerX - sourcePos.centerX) * 0.4 - width / 2;
            } else {
                x = startX;
            }
        } else if (targetCampaign) {
            // Bridge within same tree - position to the RIGHT of the campaign
            const targetPos = campaignPositions[targetCampaign];
            if (targetPos) {
                x = targetPos.x + nodeDimensions.campaign.width + horizontalGap;
            } else {
                x = startX;
            }
        } else if (sourceCampaign) {
            // Only has source - position to the right of source
            const sourcePos = campaignPositions[sourceCampaign];
            if (sourcePos) {
                x = sourcePos.x + nodeDimensions.campaign.width + horizontalGap;
            } else {
                x = startX;
            }
        } else {
            x = startX;
        }

        positionedNodes[bridgeNode.id] = {
            ...bridgeNode,
            position: { x, y: levelY[level] }
        };
    });

    // Step 3: Position AdSets - center under their inputs
    const adsets = nodes.filter(n => n.type === 'adset');

    adsets.forEach(adset => {
        const sources = edgesByTarget[adset.id] || [];
        const sourcePositions = [];

        sources.forEach(sourceId => {
            const sourcePos = positionedNodes[sourceId];
            if (sourcePos) {
                const sourceNode = nodeMap.get(sourceId);
                const sourceWidth = nodeDimensions[sourceNode?.type]?.width || 200;
                sourcePositions.push(sourcePos.position.x + sourceWidth / 2);
            }
        });

        let x;
        if (sourcePositions.length > 0) {
            // Center under all inputs
            const avgX = sourcePositions.reduce((sum, px) => sum + px, 0) / sourcePositions.length;
            x = avgX - nodeDimensions.adset.width / 2;
        } else {
            // No inputs - position at start
            x = startX;
        }

        positionedNodes[adset.id] = {
            ...adset,
            position: { x, y: levelY[3] }
        };
    });

    // Step 4: Position ABTests under their parent AdSet
    const abtests = nodes.filter(n => n.type === 'abtest');

    abtests.forEach(abtest => {
        const sources = edgesByTarget[abtest.id] || [];
        let x = startX;

        for (const sourceId of sources) {
            const sourcePos = positionedNodes[sourceId];
            if (sourcePos) {
                const sourceWidth = nodeDimensions.adset?.width || 200;
                x = sourcePos.position.x + (sourceWidth - nodeDimensions.abtest.width) / 2;
                break;
            }
        }

        positionedNodes[abtest.id] = {
            ...abtest,
            position: { x, y: levelY[4] }
        };
    });

    // Step 5: Position Ads under their parent AdSet or ABTest
    const ads = nodes.filter(n => n.type === 'ad');

    ads.forEach((ad, idx) => {
        const sources = edgesByTarget[ad.id] || [];
        let x = startX;

        for (const sourceId of sources) {
            const sourcePos = positionedNodes[sourceId];
            const sourceNode = nodeMap.get(sourceId);
            if (sourcePos && sourceNode) {
                const sourceWidth = nodeDimensions[sourceNode.type]?.width || 200;
                // Offset multiple ads from same parent
                const siblingAds = ads.filter(a => {
                    const aSources = edgesByTarget[a.id] || [];
                    return aSources.includes(sourceId);
                });
                const siblingIdx = siblingAds.indexOf(ad);
                const totalWidth = siblingAds.length * nodeDimensions.ad.width +
                                  (siblingAds.length - 1) * horizontalGap;
                const startOffset = (sourceWidth - totalWidth) / 2;
                x = sourcePos.position.x + startOffset +
                    siblingIdx * (nodeDimensions.ad.width + horizontalGap);
                break;
            }
        }

        positionedNodes[ad.id] = {
            ...ad,
            position: { x, y: levelY[5] }
        };
    });

    // Handle orphan nodes
    const orphanNodes = nodes.filter(n => !positionedNodes[n.id]);
    let orphanX = campaignX + 100;

    orphanNodes.forEach(node => {
        const level = typeToLevel[node.type] ?? 5;
        const width = nodeDimensions[node.type]?.width || 200;

        positionedNodes[node.id] = {
            ...node,
            position: { x: orphanX, y: levelY[level] }
        };
        orphanX += width + horizontalGap;
    });

    return Object.values(positionedNodes);
};

/**
 * Calculate audience flow metrics between stages
 */
export const calculateAudienceFlow = (nodes, segments, retargetingPools) => {
    const flow = {
        TOFU_TO_MOFU: { pools: [], segments: [], estimatedFlow: 0 },
        MOFU_TO_BOFU: { pools: [], segments: [], estimatedFlow: 0 }
    };

    // Count retargeting pools by stage transition
    retargetingPools.forEach(pool => {
        if (pool.sourceStage === 'TOFU' && pool.targetStage === 'MOFU') {
            flow.TOFU_TO_MOFU.pools.push(pool);
            flow.TOFU_TO_MOFU.estimatedFlow += pool.estimatedSize || 0;
        } else if (pool.sourceStage === 'MOFU' && pool.targetStage === 'BOFU') {
            flow.MOFU_TO_BOFU.pools.push(pool);
            flow.MOFU_TO_BOFU.estimatedFlow += pool.estimatedSize || 0;
        }
    });

    // Count segments by target stage
    segments.forEach(seg => {
        const sources = seg.sourceStages || [];
        if (sources.includes('TOFU') && seg.funnelStage === 'MOFU') {
            flow.TOFU_TO_MOFU.segments.push(seg);
        } else if (sources.includes('MOFU') && seg.funnelStage === 'BOFU') {
            flow.MOFU_TO_BOFU.segments.push(seg);
        }
    });

    return flow;
};

/**
 * Validate segment connections in the funnel
 */
export const validateSegmentConnections = (nodes, edges, segments) => {
    const warnings = [];
    const segmentNodes = nodes.filter(n => n.type === 'segment');

    segmentNodes.forEach(node => {
        // Check if segment has source connections
        const hasSourceConnection = edges.some(e => e.target === node.id);
        if (!hasSourceConnection) {
            warnings.push(`Segment "${node.data.name}" has no source campaigns feeding it`);
        }

        // Check if segment is being targeted
        const hasTargetConnection = edges.some(e => e.source === node.id);
        if (!hasTargetConnection) {
            warnings.push(`Segment "${node.data.name}" is not being targeted by any ad sets`);
        }
    });

    return { isValid: warnings.length === 0, warnings };
};

/**
 * Calculate creative theme consistency score
 */
export const calculateThemeConsistency = (theme, nodes) => {
    if (!theme || !nodes.length) return { score: 0, issues: [] };

    const issues = [];
    let totalChecks = 0;
    let passedChecks = 0;

    const assignedCampaigns = theme.assignedCampaigns || [];
    const assignedAds = theme.assignedAds || [];

    // Check CTA consistency
    const adNodes = nodes.filter(n => n.type === 'ad' && assignedAds.includes(n.id));
    adNodes.forEach(ad => {
        const stage = ad.data.funnelStage || 'TOFU';
        const expectedCta = theme.messaging?.ctaProgression?.[stage];
        totalChecks++;

        if (expectedCta && ad.data.cta !== expectedCta) {
            issues.push(`Ad "${ad.data.name}" CTA doesn't match theme (expected: ${expectedCta})`);
        } else {
            passedChecks++;
        }
    });

    // Check messaging tone (simplified - would need NLP in real implementation)
    if (theme.messaging?.keyMessages?.length > 0) {
        adNodes.forEach(ad => {
            totalChecks++;
            const hasKeyMessage = theme.messaging.keyMessages.some(msg =>
                ad.data.primaryText?.toLowerCase().includes(msg.toLowerCase()) ||
                ad.data.headline?.toLowerCase().includes(msg.toLowerCase())
            );
            if (!hasKeyMessage) {
                issues.push(`Ad "${ad.data.name}" doesn't include key messaging`);
            } else {
                passedChecks++;
            }
        });
    }

    const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;

    return { score, issues, totalChecks, passedChecks };
};

/**
 * Get optimal budget split based on stage performance
 */
export const getOptimalBudgetSplit = (performanceData, rules = {}) => {
    const { minSpendPerStage = 10 } = rules;
    const defaultSplit = { TOFU: 40, MOFU: 35, BOFU: 25 };

    if (!performanceData) return defaultSplit;

    // Calculate efficiency scores
    const scores = {};
    let totalScore = 0;

    ['TOFU', 'MOFU', 'BOFU'].forEach(stage => {
        const data = performanceData[stage];
        if (!data) {
            scores[stage] = 1;
        } else {
            // Higher ROAS and lower CPA = better score
            const roasScore = (data.roas || 1) / 4; // Normalize assuming 4x is excellent
            const cpaScore = data.cpa ? Math.max(0, 1 - (data.cpa / 100)) : 0.5;
            scores[stage] = (roasScore + cpaScore) / 2;
        }
        totalScore += scores[stage];
    });

    if (totalScore === 0) return defaultSplit;

    // Calculate proportional allocation
    const remaining = 100 - (minSpendPerStage * 3);
    const result = {};

    ['TOFU', 'MOFU', 'BOFU'].forEach(stage => {
        result[stage] = Math.round(minSpendPerStage + (scores[stage] / totalScore) * remaining);
    });

    // Adjust for rounding errors
    const total = result.TOFU + result.MOFU + result.BOFU;
    if (total !== 100) {
        result.TOFU += 100 - total;
    }

    return result;
};

/**
 * Get segments by stage
 */
export const getSegmentsByStage = (segments, stage) => {
    return segments.filter(s => s.funnelStage === stage);
};

/**
 * Generate retargeting pool from stage engagement
 */
export const generateRetargetingPoolConfig = (sourceStage, targetStage, sourceCampaigns = []) => {
    return {
        name: `${sourceStage} → ${targetStage} Engagers`,
        poolType: 'engagement',
        sourceStage,
        targetStage,
        sourceCampaigns,
        trigger: {
            type: 'engagement',
            threshold: 1,
            lookbackDays: 30
        },
        isActive: true
    };
};

export default {
    FUNNEL_STAGES,
    OBJECTIVE_TO_STAGE,
    calculateTotalBudget,
    calculateBudgetByStage,
    calculateBudgetByPlatform,
    validateFunnelStructure,
    groupNodesByStage,
    generateNodeId,
    getSuggestedStage,
    estimateReach,
    formatBudget,
    getFunnelStats,
    createRetargetingEdge,
    autoLayoutNodes,
    calculateAudienceFlow,
    validateSegmentConnections,
    calculateThemeConsistency,
    getOptimalBudgetSplit,
    getSegmentsByStage,
    generateRetargetingPoolConfig
};
